/**
 * Environment Configuration
 * 
 * Centralized configuration for all environment variables.
 * This module provides type-safe access to environment variables
 * and includes validation and fallback values.
 */

// Type definitions for environment variables
interface EnvironmentConfig {
  // API Configuration
  API_BASE_URL: string;
  API_TIMEOUT: number;

  // Auth0 Configuration
  AUTH0_DOMAIN: string;
  AUTH0_CLIENT_ID: string;
  AUTH0_AUDIENCE: string;
  AUTH0_REDIRECT_URI: string;

  // Application Configuration
  APP_NAME: string;
  APP_VERSION: string;
  APP_ENVIRONMENT: 'local' | 'dev' | 'stg' | 'prd' | 'prod';

  // Feature Flags
  ENABLE_MOCK_DATA: boolean;
  ENABLE_DEBUG_MODE: boolean;
  ADMIN_TESTING_UPDATE: boolean;

  // UI Configuration
  TOAST_DURATION: number;
  TOAST_POSITION: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

  // Pagination Configuration
  DEFAULT_PAGE_SIZE: number;
  MAX_PAGE_SIZE: number;
}

/**
 * Safely get environment variable with fallback
 */
function getEnvVar(key: string, fallback: string = ''): string {
  return import.meta.env[key] || fallback;
}

/**
 * Get boolean environment variable
 */
function getBooleanEnvVar(key: string, fallback: boolean = false): boolean {
  const value = import.meta.env[key];
  if (value === undefined) return fallback;
  return value === 'true' || value === '1';
}

/**
 * Get number environment variable
 */
function getNumberEnvVar(key: string, fallback: number): number {
  const value = import.meta.env[key];
  if (value === undefined) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

/**
 * Validate required environment variables
 */
function validateRequiredEnvVars(): void {
  const requiredVars = [
    'VITE_AUTH0_DOMAIN',
    'VITE_AUTH0_CLIENT_ID',
    'VITE_API_BASE_URL'
  ];

  const missingVars = requiredVars.filter(varName => !import.meta.env[varName]);

  if (missingVars.length > 0) {
    console.error('Missing required environment variables:', missingVars);
    // In development, we can continue with warnings
    if (import.meta.env.MODE === 'production') {
      throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
    }
  }
}

// Validate environment variables on module load
validateRequiredEnvVars();

/**
 * Environment configuration object
 * All environment variables are accessed through this centralized configuration
 */
export const env: EnvironmentConfig = {
  // API Configuration
  API_BASE_URL: getEnvVar('VITE_API_BASE_URL', 'http://localhost:8080'),
  API_TIMEOUT: getNumberEnvVar('VITE_API_TIMEOUT', 10000),

  // Auth0 Configuration
  AUTH0_DOMAIN: getEnvVar('VITE_AUTH0_DOMAIN'),
  AUTH0_CLIENT_ID: getEnvVar('VITE_AUTH0_CLIENT_ID'),
  AUTH0_AUDIENCE: getEnvVar('VITE_AUTH0_AUDIENCE'),
  AUTH0_REDIRECT_URI: getEnvVar('VITE_AUTH0_REDIRECT_URI', window.location.origin),

  // Application Configuration
  APP_NAME: getEnvVar('VITE_APP_NAME', 'Engage AI'),
  APP_VERSION: getEnvVar('VITE_APP_VERSION', '1.0.0'),
  APP_ENVIRONMENT: getEnvVar('VITE_APP_ENVIRONMENT', 'local') as EnvironmentConfig['APP_ENVIRONMENT'],

  // Feature Flags
  ENABLE_MOCK_DATA: getBooleanEnvVar('VITE_ENABLE_MOCK_DATA', false),
  ENABLE_DEBUG_MODE: getBooleanEnvVar('VITE_ENABLE_DEBUG_MODE', false),
  ADMIN_TESTING_UPDATE: getBooleanEnvVar('VITE_ADMIN_TESTING_UPDATE', false),

  // UI Configuration
  TOAST_DURATION: getNumberEnvVar('VITE_TOAST_DURATION', 3000),
  TOAST_POSITION: getEnvVar('VITE_TOAST_POSITION', 'bottom-right') as EnvironmentConfig['TOAST_POSITION'],

  // Pagination Configuration
  DEFAULT_PAGE_SIZE: getNumberEnvVar('VITE_DEFAULT_PAGE_SIZE', 20),
  MAX_PAGE_SIZE: getNumberEnvVar('VITE_MAX_PAGE_SIZE', 100),
};

/**
 * Utility functions for environment-specific behavior
 */
export const envUtils = {
  isLocal: () => env.APP_ENVIRONMENT === 'local',
  isDevelopment: () => env.APP_ENVIRONMENT === 'dev',
  isStaging: () => env.APP_ENVIRONMENT === 'stg',
  isProduction: () => env.APP_ENVIRONMENT === 'prd' || env.APP_ENVIRONMENT === 'prod',

  // Debug logging (only in development/staging)
  debugLog: () => {
    // Debug logging disabled
  },

  // Feature flag checker
  isFeatureEnabled: (feature: keyof Pick<EnvironmentConfig, 'ENABLE_MOCK_DATA' | 'ENABLE_DEBUG_MODE' | 'ADMIN_TESTING_UPDATE'>) => {
    return env[feature];
  }
};
